import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ArrowRight, BookOpen, Microscope, HeartPulse, CheckCircle } from 'lucide-react';

const sections = [
  {
    id: 'aesthetic',
    title: 'Aesthetic Medicine',
    exam: 'CBEAM',
    examFull: 'Competency-Based Examination in Aesthetic Medicine',
    desc: 'Clinical aesthetic procedures including injectables, energy-based devices, and facial rejuvenation.',
    to: '/faculty/aesthetic-medicine',
    domains: ['Facial Anatomy', 'Botulinum Toxin & Fillers', 'Laser & Energy-Based Devices', 'Complication Management'],
  },
  {
    id: 'longevity',
    title: 'Longevity & Anti-Aging Medicine',
    exam: 'CBELM',
    examFull: 'Competency-Based Examination in Longevity Medicine',
    desc: 'Hormonal optimisation, metabolic medicine, preventive protocols, and functional approaches to ageing.',
    to: '/faculty/longevity-medicine',
    domains: ['Hormone Optimization', 'Metabolic Medicine', 'Preventive Protocols', 'Nutrition & Lifestyle Medicine'],
  },
  {
    id: 'regenerative',
    title: 'Regenerative & Cellular Medicine',
    exam: 'CBERM',
    examFull: 'Competency-Based Examination in Regenerative Medicine',
    desc: 'PRP, growth factors, biostimulators, tissue regeneration, and cellular therapy applications.',
    to: '/faculty/regenerative-medicine',
    domains: ['PRP & Growth Factors', 'Biostimulators', 'Tissue Regeneration', 'Cellular Therapy Ethics'],
  },
];

export default function FacultyPage() {
  return (
    <div data-testid="faculty-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">Division of Competency-Based Examination & Certification</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">
            Faculty of Aesthetic & Regenerative Medicine
          </h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
          <p className="text-lg text-slate-300 mt-4 max-w-3xl">
            The Faculty oversees specialty-based competency assessment in aesthetic, longevity, and regenerative medical sciences.
          </p>
        </div>
      </section>

      {/* Faculty Mandate */}
      <section className="imcac-section bg-white">
        <div className="imcac-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <p className="imcac-overline mb-3">Faculty Mandate</p>
              <h2 className="text-3xl font-semibold text-[#1B2A4A] font-heading mb-6">
                Specialty Competency Assessment
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-4">
                The Faculty of Aesthetic & Regenerative Medicine oversees specialty-based competency assessment
                in aesthetic, longevity, and regenerative medical sciences. Operating under the Division of
                Competency-Based Examination & Certification, the Faculty develops examination blueprints,
                OSCE frameworks, and certification standards aligned with international competency models.
              </p>

              <h3 className="text-xl font-semibold text-[#1B2A4A] font-heading mb-4">Scope of Practice</h3>
              <div className="space-y-3 mb-6">
                {[
                  'Clinical Aesthetic Medicine',
                  'Anti-Aging & Longevity Medicine',
                  'Regenerative & Cellular Medicine',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-[#F8FAFC] rounded-sm border border-slate-100">
                    <CheckCircle className="h-4 w-4 text-[#C5A572]" />
                    <span className="text-sm font-medium text-[#1B2A4A]">{item}</span>
                  </div>
                ))}
              </div>

              <h3 className="text-xl font-semibold text-[#1B2A4A] font-heading mb-4">Faculty Responsibilities</h3>
              <div className="space-y-2 mb-6">
                {[
                  'Development of examination blueprints for each specialty section',
                  'Design and governance of OSCE frameworks',
                  'Certification standards aligned with international competency models',
                  'Continuous review and updating of assessment criteria',
                  'Appointment and calibration of specialist examiners',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#C5A572] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <img
                src="https://customer-assets.emergentagent.com/job_cbeam-portal/artifacts/owme7yia_DSC_1416.JPG"
                alt="IMCAC Faculty"
                className="w-full h-64 object-cover rounded-sm shadow-md mb-6"
              />
              <Card className="imcac-card border-t-2 border-t-[#C5A572]">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-3">Governance</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    The Faculty operates independently from training providers to ensure examination integrity
                    and impartial certification standards. All examination content is developed and validated
                    by subject matter experts with no conflicts of interest.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Three Sections */}
      <section className="imcac-section bg-[#F8FAFC]">
        <div className="imcac-container">
          <div className="text-center mb-12">
            <p className="imcac-overline mb-3">Specialty Sections</p>
            <h2 className="text-3xl font-semibold text-[#1B2A4A] font-heading">Examination Sections</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {sections.map((section, i) => (
              <Card key={section.id} className="imcac-card overflow-hidden" data-testid={`section-${section.id}`}>
                <div className="bg-[#1B2A4A] px-6 py-5">
                  <Badge className="bg-[#C5A572] text-white text-xs mb-2">{section.exam}</Badge>
                  <h3 className="text-lg font-semibold text-white font-heading">{section.title}</h3>
                  <p className="text-xs text-slate-300 mt-1">{section.examFull}</p>
                </div>
                <CardContent className="p-6">
                  <p className="text-sm text-slate-600 leading-relaxed mb-4">{section.desc}</p>
                  <p className="text-xs font-bold uppercase tracking-widest text-[#C5A572] mb-2">Core Domains</p>
                  <ul className="space-y-1.5 mb-5">
                    {section.domains.map((d, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm text-slate-700">
                        <span className="w-1.5 h-1.5 bg-[#C5A572] rounded-full" />
                        {d}
                      </li>
                    ))}
                  </ul>
                  <Link to={section.to} data-testid={`explore-${section.id}`}>
                    <Button variant="outline" className="w-full rounded-sm border-[#1B2A4A] text-[#1B2A4A] text-sm">
                      Explore {section.exam} <ArrowRight className="ml-2 h-3.5 w-3.5" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-10 bg-white">
        <div className="imcac-container">
          <div className="bg-[#F8FAFC] border border-slate-200 rounded-sm p-6">
            <p className="text-sm text-slate-600 leading-relaxed">
              <span className="text-[#C5A572] font-semibold">Legal Disclaimer:</span> Certification validates
              assessed competencies within defined domains and does not grant medical licensure. Candidates must
              hold valid professional registration in their jurisdiction.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
